import {
  Directive,
  ElementRef,
  Host,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  Self
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Calendar } from 'primeng/calendar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[sigvPrimeCalendarInputFormatValidate]',
})
export class PrimeCalendarInputFormatValidateDirective
  implements OnInit, OnDestroy{
  private readonly destroy$ = new Subject();
  private errorSpanElement?: Node;
  private defaultAutoDisappearDelay = 3000;
  private timer: ReturnType<typeof setTimeout> | null = null;

  isTyping = false;

  /**
   * @description 設定格式錯誤訊息自動消失時間，單位為毫秒。非必填，預設值為 3000，單位為 ms。
   */
  @Input('sigvPrimeCalendarInputFormatValidate') autoDisappearDelay?: string;

  constructor(
    @Host() @Self() @Optional() private calendar: Calendar,
    private el: ElementRef,
    public renderer: Renderer2,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.calendar) {
      this.calendar.onFocus.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.timer) {
          window.clearTimeout(this.timer);
          this.timer = null;
        }
        this.removeErrorSpan();
        this.updateValidateClass(true);
      });
      this.calendar.onInput.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.isTyping = true;
      });
      this.calendar.onBlur.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.isTyping) {
          // 驗證元件的值是否為正常的日期, 沒有值表示輸入資料不正確，顯示錯誤訊息
          if (!this.calendar.value) {
            this.appendErrorSpan();
            this.updateValidateClass(false);
            const delay = this.getAutoDisapperDelayValue();
            if (delay > 0) {
              this.timer = setTimeout(() => {
                this.removeErrorSpan();
                this.updateValidateClass(true);
                this.timer = null;
              }, delay);
            }
          }
        }
        this.isTyping = false;
      });
    }
  }

  private appendErrorSpan(): void {
    const el: HTMLElement = this.el.nativeElement;
    const errorSpan = this.renderer.createElement('small');
    const errorText = this.renderer.createText(
      this.translateService.instant('SigvLibrary.InvalidDate')
    );
    this.renderer.addClass(errorSpan, 'p-error');
    this.renderer.appendChild(errorSpan, errorText);
    this.errorSpanElement = errorSpan;
    if (el.parentNode && this.errorSpanElement) {
      el.parentNode?.appendChild(this.errorSpanElement);
    }
  }

  private removeErrorSpan(): void {
    if (this.errorSpanElement) {
      const el: HTMLElement = this.el.nativeElement;
      el.parentNode?.removeChild(this.errorSpanElement);
      this.errorSpanElement = undefined;
    }
  }

  private updateValidateClass(isValidate: boolean): void {
    if (isValidate) {
      this.renderer.removeClass(this.el.nativeElement, 'ng-invalid');
      this.renderer.removeClass(this.el.nativeElement, 'ng-touched');
      this.renderer.removeClass(this.el.nativeElement, 'ng-pristine');
    } else {
      this.renderer.addClass(this.el.nativeElement, 'ng-invalid');
      this.renderer.addClass(this.el.nativeElement, 'ng-touched');
      this.renderer.addClass(this.el.nativeElement, 'ng-pristine');
    }
  }

  private getAutoDisapperDelayValue(): number {
    const numberRegex = /^[0-9]*$/;
    if (this.autoDisappearDelay && numberRegex.test(this.autoDisappearDelay)) {
      return parseInt(this.autoDisappearDelay, 10);
    } else {
      return this.defaultAutoDisappearDelay;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
